<template>
  <div class="bg-dark-bg text-white">
    <!-- Hero Section - Brutalist -->
    <section class="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div class="max-w-7xl w-full">
        <!-- Main Title with Hover Blurb -->
        <div
          class="relative group cursor-default"
          @mouseenter="showBlurb = true"
          @mouseleave="showBlurb = false"
        >
          <h1 class="font-display text-brutalist text-white leading-none uppercase tracking-tighter mb-8">
            AI • IT<br/>
            Consulting
          </h1>

          <!-- Dropdown Blurb -->
          <Transition name="slide-fade">
            <div
              v-show="showBlurb"
              class="absolute left-0 top-full mt-4 max-w-2xl bg-dark-surface border-2 border-accent-neon p-6 z-10"
            >
              <p class="text-lg font-light text-gray-300 leading-relaxed">
                After 18 months navigating the AI landscape, the real wins don't come from shiny tools –
                they come from solid foundations. Securing data, untangling workflows, and making the tech
                you already own actually work for you.
              </p>
            </div>
          </Transition>
        </div>

        <!-- Subtitle -->
        <p class="text-2xl md:text-3xl text-gray-400 max-w-3xl mb-16 font-light">
          Fix tech problems without creating new ones
        </p>
      </div>

      <!-- Accent Elements -->
      <div class="absolute top-0 right-0 w-96 h-96 bg-accent-neon opacity-5 blur-3xl"></div>
    </section>

    <!-- Services Section - Interactive Pillars -->
    <section class="min-h-screen py-32 px-4 sm:px-6 lg:px-8 bg-dark-surface">
      <div class="max-w-7xl mx-auto">
        <div class="grid grid-cols-1 gap-0">
          <!-- Service Pillar 1 -->
          <div
            class="border-t-2 border-white py-16 group cursor-pointer transition-all duration-300 hover:bg-dark-bg"
            @mouseenter="activeService = 'it'"
            @mouseleave="activeService = null"
          >
            <div class="flex items-start justify-between">
              <h2 class="text-brutalist-md uppercase group-hover:text-accent-neon transition-colors duration-300">
                IT Consulting
              </h2>
              <span class="text-6xl font-light text-gray-600 group-hover:text-accent-neon transition-colors duration-300">
                {{ activeService === 'it' ? '−' : '+' }}
              </span>
            </div>

            <Transition name="expand">
              <div v-show="activeService === 'it'" class="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl">
                <div v-for="item in itExamples" :key="item" class="border-l-2 border-accent-neon pl-4">
                  <p class="text-lg text-gray-300">{{ item }}</p>
                </div>
              </div>
            </Transition>
          </div>

          <!-- Service Pillar 2 -->
          <div
            class="border-t-2 border-white py-16 group cursor-pointer transition-all duration-300 hover:bg-dark-bg"
            @mouseenter="activeService = 'ai'"
            @mouseleave="activeService = null"
          >
            <div class="flex items-start justify-between">
              <h2 class="text-brutalist-md uppercase group-hover:text-accent-neon transition-colors duration-300">
                AI Integration
              </h2>
              <span class="text-6xl font-light text-gray-600 group-hover:text-accent-neon transition-colors duration-300">
                {{ activeService === 'ai' ? '−' : '+' }}
              </span>
            </div>

            <Transition name="expand">
              <div v-show="activeService === 'ai'" class="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl">
                <div v-for="item in aiExamples" :key="item" class="border-l-2 border-accent-neon pl-4">
                  <p class="text-lg text-gray-300">{{ item }}</p>
                </div>
              </div>
            </Transition>
          </div>

          <!-- Service Pillar 3 -->
          <div
            class="border-t-2 border-white border-b-2 py-16 group cursor-pointer transition-all duration-300 hover:bg-dark-bg"
            @mouseenter="activeService = 'business'"
            @mouseleave="activeService = null"
          >
            <div class="flex items-start justify-between">
              <h2 class="text-brutalist-md uppercase group-hover:text-accent-neon transition-colors duration-300">
                Business Solutions
              </h2>
              <span class="text-6xl font-light text-gray-600 group-hover:text-accent-neon transition-colors duration-300">
                {{ activeService === 'business' ? '−' : '+' }}
              </span>
            </div>

            <Transition name="expand">
              <div v-show="activeService === 'business'" class="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl">
                <div v-for="item in businessExamples" :key="item" class="border-l-2 border-accent-neon pl-4">
                  <p class="text-lg text-gray-300">{{ item }}</p>
                </div>
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-32 px-4 sm:px-6 lg:px-8 bg-dark-bg border-t-2 border-white">
      <div class="max-w-7xl mx-auto text-center">
        <h2 class="text-4xl md:text-6xl font-bold text-white mb-8 uppercase tracking-tight">
          Let's kōrero
        </h2>
        <p class="text-xl text-gray-400 mb-12 max-w-2xl mx-auto font-light">
          If complexity feels like chaos, I treat it like a puzzle
        </p>
        <RouterLink
          to="/contact"
          class="inline-block bg-accent-neon text-dark-bg font-bold py-4 px-12 text-lg uppercase tracking-wide hover:bg-white transition-colors duration-300"
        >
          Get in touch
        </RouterLink>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink } from 'vue-router'

const showBlurb = ref(false)
const activeService = ref<string | null>(null)

const itExamples = [
  'Microsoft 365 security & compliance',
  'System audits & workflow mapping',
  'Cut subscription bloat',
  'Privacy Act compliant operations',
]

const aiExamples = [
  'Automation that serves you, not vendors',
  'No data leaks, no lock-in',
  'Straight talk – sometimes it\'s a better spreadsheet',
  'Iteration over mastery',
]

const businessExamples = [
  'Fractional Tech Ops for NFPs & SMEs',
  'Cross-Tasman government projects',
  'APAC operations experience',
  'Tight budgets, big missions',
]
</script>

<style scoped>
/* Slide Fade Transition */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Expand Transition */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.4s ease;
  max-height: 500px;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
